import classNames from "classnames";
import type { FC } from "react";
import { useContext, useEffect, useMemo, useRef } from "react";
import type { IconType } from "react-icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    HiHome,
    HiChartBar,
    HiUsers,
    HiUserGroup,
    HiBriefcase,
    HiClock,
    HiBookOpen,
    HiCog,
    HiPlusCircle,
    HiTicket,
    HiInbox,
    HiChatAlt2,
    HiTemplate,
    HiViewGrid,
    HiRefresh,
    HiLogout,
    HiSparkles,
    HiSpeakerphone,
    HiOfficeBuilding,
} from "react-icons/hi";
import { useSidebarContext } from "../context/SidebarContext";
import { ChatContext, ChatContextInterface } from "./MSRContext";
import isSmallScreen from "../helpers/is-small-screen";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { agentRoles, agentRoleLabels } from "../constants/agent";
import { useEffectiveRoles } from "../hooks/useEffectiveRoles";

interface SidebarLink {
    type?: "link";
    label: string;
    icon: IconType;
    path: string;
    badge?: string | number;
    accent: string;
    /** Roles that may see this item. If omitted the item is visible to everyone. */
    roles?: number[];
}

interface SidebarSection {
    type: "section";
    label: string;
}

type SidebarItem = SidebarLink | SidebarSection;

const CompanyAdminSidebar: FC = function () {
    const { setWrapper, setIsAuthenticated, wrapper } = useContext(
        ChatContext
    ) as ChatContextInterface;
    const { isOpenOnSmallScreens, setOpenOnSmallScreens } = useSidebarContext();
    const isMobile = isSmallScreen();
    const { t } = useTranslation("nav");
    const currentUser = useCurrentUser();
    const effectiveRoles = useEffectiveRoles();

    const location = useLocation();
    const currentPage = location.pathname;
    const navigate = useNavigate();
    const activeLinkRef = useRef<HTMLAnchorElement | null>(null);

    useEffect(() => {
        activeLinkRef.current?.scrollIntoView({ block: "nearest" });
    }, []);

    const handleLogout = () => {
        wrapper?.closeConnection();
        sessionStorage.clear();
        localStorage.clear();
        setWrapper(null);
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
        window.location.reload();
    };

    const isActive = (path: string, exact = false) =>
        exact ? currentPage === path : currentPage.startsWith(path);

    // Admin tier: management + company_admin + super_admin.
    // These items aren't shown to frontline/backoffice.
    const ADMIN = [
        agentRoles.management,
        agentRoles.company_admin,
        agentRoles.super_admin,
    ];

    const isSuperAdmin = currentUser?.agent_role === agentRoles.super_admin;
    const SUPER_ADMIN_PATHS = new Set<string>([
        "/", 
        "/reports", 
        "/agents", 
        "/groups",
        "/clients",
        "/companies",
    ]);

    const items: SidebarItem[] = [
        { type: "section", label: t("section.overview") },
        {
            label: t("menu.dashboard"),
            icon: HiHome,
            path: "/",
            accent: "from-blue-500 to-indigo-500",
            roles: ADMIN,
        },
        {
            label: t("menu.reports"),
            icon: HiChartBar,
            path: "/reports",
            accent: "from-amber-400 to-orange-500",
            roles: ADMIN,
        },
        {
            label: t("menu.requests"),
            icon: HiInbox,
            path: "/requestList",
            accent: "from-cyan-400 to-blue-600",
        },
        {
            label: t("menu.chats"),
            icon: HiChatAlt2,
            path: "/chat",
            accent: "from-sky-500 to-blue-600",
        },
        {
            label: t("menu.agents"),
            icon: HiUsers,
            path: "/agentList",
            accent: "from-purple-500 to-pink-500",
        },

        { type: "section", label: t("section.management") },
        {
            label: t("menu.tickets"),
            icon: HiTicket,
            path: "/tickets",
            accent: "from-purple-900 to-purple-500",
        },
        {
            // Visible to everyone. The page itself scopes the table: normal
            // agents see only their own group's agents, while Managers and
            // Group Admins see all agents.
            label: t("menu.agentManagement"),
            icon: HiUsers,
            path: "/agents",
            accent: "from-indigo-500 to-violet-500",
        },
        {
            label: t("menu.groups"),
            icon: HiUserGroup,
            path: "/groups",
            accent: "from-pink-500 to-rose-500",
            roles: ADMIN,
        },
        {
            label: t("menu.workShifts"),
            icon: HiClock,
            path: "/work-shifts",
            accent: "from-lime-400 to-green-600",
            roles: ADMIN,
        },
        {
            label: t("menu.clients"),
            icon: HiBriefcase,
            path: "/clients",
            accent: "from-orange-400 to-pink-500",
        },
        {
            label: t("menu.clientGroups"),
            icon: HiUserGroup,
            path: "/client-groups",
            accent: "from-yellow-400 to-orange-500",
        },
        {
            label: t("menu.categories"),
            icon: HiViewGrid,
            path: "/categories",
            accent: "from-teal-400 to-blue-500",
        },
        {
            label: t("menu.subcategories"),
            icon: HiTemplate,
            path: "/subcategories",
            accent: "from-green-400 to-teal-500",
        },
        {
            label: t("menu.updateReasons"),
            icon: HiRefresh,
            path: "/update-reasons",
            accent: "from-fuchsia-500 to-pink-500",
        },
        {
            label: t("menu.knowledgeBase"),
            icon: HiBookOpen,
            path: "/knowledge-base",
            accent: "from-emerald-500 to-cyan-500",
            roles: ADMIN,
        },
        {
            label: t("menu.textToSpeech"),
            icon: HiSpeakerphone,
            path: "/text-to-speech",
            accent: "from-blue-500 to-cyan-500",
            roles: ADMIN,
        },

        { type: "section", label: t("section.superAdmin") },
        {
            label: t("menu.companies"),
            icon: HiOfficeBuilding,
            path: "/companies",
            accent: "from-indigo-500 to-violet-500",
            roles: [agentRoles.super_admin],
        },

        { type: "section", label: t("section.settings") },
        {
            label: t("menu.companySettings"),
            icon: HiCog,
            path: "/company-settings",
            accent: "from-slate-500 to-zinc-600",
            // visible to everyone — AI Agent CUD locked at the page level.
        },
        {
            label: t("menu.createCompany"),
            icon: HiPlusCircle,
            path: "/company-settings/create",
            accent: "from-emerald-500 to-teal-600",
            roles: [agentRoles.super_admin],
        },
    ];

    // Filter items by effective roles, drop empty sections
    const visibleItems = useMemo<SidebarItem[]>(() => {
        const filtered: SidebarItem[] = [];
        for (const item of items) {
            if (item.type === "section") {
                filtered.push(item);
                continue;
            }
            const link = item as SidebarLink;
            if (isSuperAdmin) {
                if (SUPER_ADMIN_PATHS.has(link.path)) {
                    filtered.push(item);
                }
                continue;
            }
            if (!link.roles || link.roles.length === 0) {
                filtered.push(item);
                continue;
            }
            if (link.roles.some((r) => effectiveRoles.has(r))) {
                filtered.push(item);
            }
        }
        // Drop sections that have no following links before the next section
        const compacted: SidebarItem[] = [];
        for (let i = 0; i < filtered.length; i++) {
            const cur = filtered[i];
            if (cur.type === "section") {
                let hasLink = false;
                for (let j = i + 1; j < filtered.length; j++) {
                    if (filtered[j].type === "section") break;
                    hasLink = true;
                    break;
                }
                if (!hasLink) continue;
            }
            compacted.push(cur);
        }
        return compacted;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveRoles, isSuperAdmin, t]);

    if (isMobile && !isOpenOnSmallScreens) {
        return null;
    }

    const displayName =
        currentUser?.display_name ||
        currentUser?.agent_username ||
        "Agent";
    const roleLabel =
        agentRoleLabels[currentUser?.agent_role ?? agentRoles.agent] || "Agent";
    const initial = displayName.charAt(0).toUpperCase();

    return (
        <>
            {isMobile && isOpenOnSmallScreens && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-20 transition-all duration-300 ease-out"
                    onClick={() => setOpenOnSmallScreens(false)}
                />
            )}

            <aside
                className={classNames(
                    "fixed left-0 top-16 bottom-0 w-64",
                    "bg-white dark:bg-gray-900",
                    "border-r border-gray-200 dark:border-gray-800",
                    "shadow-xl shadow-black/5 dark:shadow-black/40",
                    "flex flex-col transition-all duration-300 ease-out",
                    {
                        "z-[100]": isMobile,
                        "z-[50]": !isMobile,
                        "transform translate-x-0 opacity-100":
                            !isMobile || isOpenOnSmallScreens,
                        "transform -translate-x-full opacity-0":
                            isMobile && !isOpenOnSmallScreens,
                    }
                )}
            >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />

                <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-800">
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg shadow-md shadow-blue-500/30">
                                {initial}
                            </div>
                            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-400 border-2 border-white dark:border-gray-900 rounded-full" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                {displayName}
                            </p>
                            <div className="flex items-center gap-1 mt-0.5">
                                <HiSparkles className="w-3 h-3 text-purple-500" />
                                <p className="text-[11px] font-medium text-purple-600 dark:text-purple-300 truncate">
                                    {roleLabel}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <nav className="flex-1 overflow-y-auto px-3 py-4">
                    <div className="space-y-1">
                        {visibleItems.map((item, index) => {
                            if (item.type === "section") {
                                return (
                                    <div
                                        key={`section-${index}`}
                                        className={classNames("pt-4 pb-1 px-2", {
                                            "pt-1": index === 0,
                                        })}
                                    >
                                        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">
                                            {item.label}
                                        </p>
                                    </div>
                                );
                            }

                            const link = item as SidebarLink;
                            const Icon = link.icon;
                            const exact = link.path === "/";
                            const active = isActive(link.path, exact);

                            return (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    ref={active ? activeLinkRef : undefined}
                                    onClick={() => {
                                        if (isMobile) {
                                            setOpenOnSmallScreens(false);
                                        }
                                    }}
                                    className={classNames(
                                        "group relative flex items-center gap-3 px-3 py-2.5 rounded-xl",
                                        "transition-all duration-200 ease-out",
                                        "focus:outline-none focus:ring-2 focus:ring-blue-500/40",
                                        {
                                            [`bg-gradient-to-r ${link.accent} text-white shadow-md shadow-blue-500/20`]:
                                                active,
                                            "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white":
                                                !active,
                                        }
                                    )}
                                >
                                    {active && (
                                        <span className="absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-white/90" />
                                    )}
                                    <span
                                        className={classNames(
                                            "flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-all",
                                            {
                                                "bg-white/20": active,
                                                "bg-gray-100 dark:bg-gray-800 group-hover:bg-white dark:group-hover:bg-gray-700":
                                                    !active,
                                            }
                                        )}
                                    >
                                        <Icon
                                            className={classNames(
                                                "transition-transform group-hover:scale-110",
                                                {
                                                    "text-white drop-shadow-sm":
                                                        active,
                                                    "text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white":
                                                        !active,
                                                }
                                            )}
                                            style={{
                                                width: "18px",
                                                height: "18px",
                                            }}
                                        />
                                    </span>
                                    <span
                                        className={classNames(
                                            "text-sm font-medium truncate flex-1",
                                            {
                                                "text-white": active,
                                            }
                                        )}
                                    >
                                        {link.label}
                                    </span>
                                    {link.badge !== undefined && (
                                        <span
                                            className={classNames(
                                                "text-[11px] font-bold px-2 py-0.5 rounded-full",
                                                {
                                                    "bg-white/25 text-white":
                                                        active,
                                                    "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300":
                                                        !active,
                                                }
                                            )}
                                        >
                                            {link.badge}
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                    </div>
                </nav>

                <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-800">
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500/40 group"
                    >
                        <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/20 group-hover:bg-red-100 dark:group-hover:bg-red-900/40 transition-colors">
                            <HiLogout className="w-4 h-4" />
                        </span>
                        <span className="text-sm font-semibold">
                            {t("logout")}
                        </span>
                    </button>
                </div>
            </aside>
        </>
    );
};

export default CompanyAdminSidebar;
